import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, Divider, forwardRef, Input, PlusOutline, rem, Text, useDisclosure, VStack } from '@bonusly/design-system';
import { Recipient } from '@bonusly/recipient-entities';
import { FeatureFlag } from '@bonusly/who-am-i';

import { useActionInput } from '../../context';
import { SelectorBox, SelectorProps } from '../selector/selector-box';
import { SelectorOption } from '../selector/selector-option';
import { useSelector } from '../selector/use-selector';
import { RecipientCSVModal } from './recipient-csv-modal';
import { RecipientOption } from './recipient-option';

const LOADING_TIME_TO_SHOW_MESSAGE = 3000;

export const RecipientSelectorWithCsvUpload = forwardRef<SelectorProps<Recipient>, 'div'>(({ command, hash, items, isLoading, query }, ref) => {
	const { t } = useTranslation('components');
	const { state } = useActionInput();
	const [showLoadingMessage, setShowLoadingMessage] = useState(false);
	const [csvEmailAddresses, setCsvEmailAddresses] = useState<string[]>([]);
	const [csvFileName, setCsvFileName] = useState<string>('');
	const csvModal = useDisclosure();
	const { getOptionProps, getSelectorProps, selectedIndex } = useSelector({
		command,
		hash,
		items,
		ref,
	});
	const hasCustomGroupRecipient = state.recipients.some((recipient) => recipient.dimension === 'csv_upload');

	useEffect(() => {
		if (isLoading) {
			const timeout = setTimeout(() => {
				setShowLoadingMessage(true);
			}, LOADING_TIME_TO_SHOW_MESSAGE);
			return () => {
				clearTimeout(timeout);
			};
		} else {
			setShowLoadingMessage(false);
		}
	}, [isLoading]);

	const loadingMessage = !showLoadingMessage ? undefined : (
		<Text color="text.softer" fontSize="sm" maxW={72} pt={2} textAlign="center">
			{t('rich_media.loading_is_taking_too_long')}
		</Text>
	);

	const handleCSVUploadClick = useCallback(() => {
		document.getElementById('csv-uploader')?.click();
	}, []);

	const handleCSVChange = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (file) {
			// Get the contents and separate by new line
			const contents = await file.text();
			const lines = contents.split('\n');

			// Trim each line
			const emailAddresses = lines.map((line) => line.trim());
			// Remove any empty lines
			const filteredEmailAddresses = emailAddresses.filter((line) => line !== '');

			setCsvEmailAddresses(filteredEmailAddresses);
			setCsvFileName(file.name);
			csvModal.onOpen();
		} else {
			setCsvEmailAddresses([]);
			setCsvFileName('');
		}
	}, []);

	const handleDeleteFile = () => {
		setCsvEmailAddresses([]);
		setCsvFileName('');
	};

	// NOTE: don't render the selector when the csv modal is open
	if (csvModal.isOpen) {
		return (
			<RecipientCSVModal
				fileName={csvFileName}
				csvEmailAddresses={csvEmailAddresses}
				handleCSVChange={handleCSVChange}
				handleDeleteFile={handleDeleteFile}
				isOpen={csvModal.isOpen}
				onClose={csvModal.onClose}
			/>
		);
	}

	// NOTE: don't render the selector when there is a custom group recipient because additional recipients are not supported
	if (hasCustomGroupRecipient) return null;

	return (
		<SelectorBox data-testid="recipient-selector" isLoading={isLoading} loadingMessage={loadingMessage}>
			<FeatureFlag flag="csv-award-recipients">
				{/* NOTE: only show if there is no recipient selected */}
				{!state.hasRecipient && (
					<Box display={{ base: 'none', xs: 'block' }}>
						<Button data-testid="csv-upload-button" variant="alpha" colorScheme="secondary" h="fit-content" p={2} mb={2} onClick={handleCSVUploadClick}>
							<PlusOutline />
							<VStack spacing={0} textAlign="left" ml={3}>
								<Text fontSize={50} fontWeight="medium" w="100%">
									{t('rich_media.uploadCsv.title')}
								</Text>
								<Text fontSize={50} color="text.softer" w="100%">
									{t('rich_media.uploadCsv.description')}
								</Text>
							</VStack>
						</Button>
						<Input id="csv-uploader" data-testid="csv-uploader" accept="text/csv" display="none" type="file" onChange={handleCSVChange} />
						<Divider />
					</Box>
				)}
			</FeatureFlag>
			<VStack as="ul" alignItems="flex-start" maxH={rem(410)} overflowY="auto" role="listbox" spacing={0} {...getSelectorProps()}>
				{!query && (
					<Box p={2} color="gray.500" fontSize="sm" fontWeight="normal">
						{t('rich_media.search_users.help_text')}
					</Box>
				)}

				{!items.length && (
					<Box p={2} color="gray.500" fontSize="sm">
						{t('rich_media.no_results')}
					</Box>
				)}

				{items.map((recipient, index) => {
					return (
						<SelectorOption isSelected={selectedIndex === index} key={recipient.id} {...getOptionProps(index)}>
							<RecipientOption recipient={recipient} />
						</SelectorOption>
					);
				})}
			</VStack>
		</SelectorBox>
	);
});
